/**
 * spaceDrawBuilders.ts
 * Internal helper builders for {@link buildSpaceDrawList}.
 *
 * Extracted from `spaceRender.ts` so the public entry-point file stays
 * under the AGENTS.md 300-line cap. None of these helpers are exported
 * outside the rendering folder — the renderer keeps a single public
 * surface (`buildSpaceDrawList`) and these helpers are exercised
 * indirectly through it.
 *
 * No game-state mutation — every builder is read-only over `spaceState`.
 *
 * @module spaceDrawBuilders
 */

import { spaceState } from '../engine/spaceState';
import {
  SPACE_ENEMY_FIGHTER_COLOR,
  SPACE_ENEMY_BOMBER_COLOR,
  SPACE_ENEMY_SATELLITE_COLOR,
  SPACE_ENEMY_STAR_EATER_COLOR,
  SPACE_FIGHTER_LASER_COLOR,
  SPACE_NEBULA_FOG_COLOR,
  SPACE_MINE_COLORS,
} from '../../constants/rendering';
import {
  SPACE_SHARD_COLOR,
  SPACE_SHARD_SPARKLE_COLOR,
  SPACE_SHARD_SPARKLE_PERIOD_S,
} from '../../constants/spaceShardConstants';
import type { SpaceEnemyKind } from '../../constants/spaceEnemyConstants';
import { VIEWPORT_WIDTH, VIEWPORT_HEIGHT } from '../../constants/gameConstants';
import type { SpaceRect } from './spaceRenderTypes';

// DECISION: The boss-specific builders (hull, turret bolts, missiles,
// shield ring) live in `spaceBossDrawBuilders.ts` and are re-exported
// here so this module stays under the 300-line cap while consumers keep
// importing every builder from `./spaceDrawBuilders` unchanged.
export * from './spaceBossDrawBuilders';

/**
 * Palette per enemy kind. Declared as a `Record<SpaceEnemyKind, string>`
 * so TypeScript fails the build the moment a new kind is added to
 * `SpaceEnemyKind` without a colour mapping here — a missing entry would
 * otherwise silently render the new kind in the fighter palette.
 */
export const SPACE_ENEMY_COLOR_BY_KIND: Record<SpaceEnemyKind, string> = {
  fighter: SPACE_ENEMY_FIGHTER_COLOR,
  bomber: SPACE_ENEMY_BOMBER_COLOR,
  satellite: SPACE_ENEMY_SATELLITE_COLOR,
  starEater: SPACE_ENEMY_STAR_EATER_COLOR,
};

/**
 * Build a full-viewport fog rect when the current level config opts into
 * nebula fog, or `null` otherwise.
 *
 * DECISION: Gated by level id (`'2-3'`) rather than a typed `fogEnabled`
 * flag on `SpaceLevelConfig`. Adding the flag would touch the level
 * types module, every level config, and the level tests — outside the
 * 5-file scope cap for this slice. Graduate to a typed flag the moment
 * a second level needs fog.
 */
export function buildFogRect(): SpaceRect | null {
  if (spaceState.levelConfig.id !== '2-3') return null;
  return {
    x: 0,
    y: 0,
    width: VIEWPORT_WIDTH,
    height: VIEWPORT_HEIGHT,
    color: SPACE_NEBULA_FOG_COLOR,
    key: 'fog',
  };
}

/**
 * Build camera-relative rectangles for every live `FighterDrone`-fired
 * laser bolt. Sibling to `buildBossProjectileRects` / `buildBossMissileRects`
 * — same shape, different source array and palette.
 *
 * @param cameraX - Current camera left-edge X.
 */
export function buildFighterDroneLaserRects(cameraX: number): SpaceRect[] {
  const out: SpaceRect[] = [];
  for (const b of spaceState.fighterLasers) {
    if (b.dead) continue;
    out.push({
      x: b.x - cameraX,
      y: b.y,
      width: b.width,
      height: b.height,
      color: SPACE_FIGHTER_LASER_COLOR,
      // DECISION: `fl` prefix keeps fighter-laser keys distinct from
      // every other bolt / missile stream (`p` / `bp` / `bm` / `m`).
      key: `fl${b.spawnId}`,
    });
  }
  return out;
}

/**
 * Build camera-relative rectangles for every live Level 2-3 homing mine.
 * Colour is keyed off `m.state` via the `SPACE_MINE_COLORS` record so the
 * IDLE→ACTIVE trigger flip is visually unambiguous. Sibling to the boss
 * projectile / missile helpers — same shape, different source array.
 *
 * @param cameraX - Current camera left-edge X.
 */
export function buildMineRects(cameraX: number): SpaceRect[] {
  const out: SpaceRect[] = [];
  for (const m of spaceState.mines) {
    if (m.dead) continue;
    out.push({
      x: m.x - cameraX,
      y: m.y,
      width: m.width,
      height: m.height,
      color: SPACE_MINE_COLORS[m.state],
      // DECISION: Same sprite for both IDLE and ACTIVE — colour tint is
      // already a strong enough state signal in the greybox-rect path,
      // and authoring two near-identical mine PNGs would be wasted
      // work. Promote to per-state ids the moment a real ACTIVE
      // glow variant ships.
      spriteId: 'mine',
      // DECISION: `m` prefix keeps mine keys distinct from the ship-bolt
      // (`p`), boss-bolt (`bp`), and boss-missile (`bm`) streams.
      key: `m${m.spawnId}`,
    });
  }
  return out;
}

/**
 * Build camera-relative rectangles for every uncollected Constellation
 * Shard in the active level. Shards are world-space stationary — the
 * camera drifts past them — so the rect's screen-x is `s.x - cameraX`
 * with no integration. The fill alternates between
 * {@link SPACE_SHARD_COLOR} and {@link SPACE_SHARD_SPARKLE_COLOR} on a
 * {@link SPACE_SHARD_SPARKLE_PERIOD_S} cycle keyed off the scene clock
 * so every shard sparkles in phase without per-instance state.
 *
 * @param cameraX - Current camera left-edge X.
 */
export function buildShardRects(cameraX: number): SpaceRect[] {
  const out: SpaceRect[] = [];
  const sparkleOn =
    (spaceState.elapsedSeconds % SPACE_SHARD_SPARKLE_PERIOD_S) >=
    SPACE_SHARD_SPARKLE_PERIOD_S / 2;
  for (const s of spaceState.shards) {
    if (s.dead || s.collected) continue;
    out.push({
      x: s.x - cameraX,
      y: s.y,
      width: s.width,
      height: s.height,
      color: sparkleOn ? SPACE_SHARD_SPARKLE_COLOR : SPACE_SHARD_COLOR,
      // DECISION: `cs` prefix (constellation-shard) keeps shard keys
      // distinct from mine (`m`), boss-bolt (`bp`), missile (`bm`),
      // and shield-node (`bs`) streams.
      key: `cs${s.spawnId}`,
    });
  }
  return out;
}
